import UnifiedLoginAnalytics from 'components/UnifiedLoginAnalytics';
import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import dynamic from 'next/dynamic';
import localFont from 'next/font/local';
import { useRouter } from 'next/router';
import { setCookie } from 'nookies';

import { createLiveChatAnalyticsEvent } from 'bx-live-chat/analytics';
import Analytics from 'components/Analytics';
import {
  BuyerIntentPageView,
  BuyerIntentSession,
  getBuyerIntentPageSourceLocation,
} from 'components/BuyerIntent';
import ExitIntentModals from 'components/ExitIntentModals';
import ExposeUserScript from 'components/ExposeUserScript';
import G2CompanyLookup from 'components/G2CompanyLookup';
import MainLayout from 'components/Layout/Main';
import OneTapAnalytics from 'components/OneTapScript/OneTapAnalytics';
import ScrollMilestones from 'components/ScrollMilestones';
import getConfig from 'config/public';
import AuthSessionAppProvider from 'context/AuthSessionAppProvider';
import { CompareBasketProvider } from 'context/CompareBasketContext';
import { ViewportProvider } from 'context/ViewportContext';
import { DDRumIntegration } from 'datadog-client/ddRumIntegration';
import VercelFlagsProvider, { useFlag } from 'featureFlags/VercelFlagsProvider';
import { CORE_FLAGS } from 'featureFlags/flags/definitions';
import { mergeExperimentsGA4 } from 'featureFlags/helpers';
import google from 'lib/AnalyticsGDM/services/google';
import OsanoConsentOverlay from 'shared-utils/OsanoConsentOverlay';

const getappIconFont = localFont({
  adjustFontFallback: false,
  variable: '--font-getapp-icon',
  src: [
    { path: '../components/Icon/fonts/getapp-font.woff2', style: 'normal' },
  ],
  preload: true,
});

import '../styles/globals.scss';
import '@gartner-digital-markets/nimbus-v3-ui/src/styles/tokens-getapp.css';
// Fix Dialog positioning for bx-forms ModalForm - Tailwind important selector doesn't apply to portals
import '../components/ExitIntentModals/ExitIntentModals.css';

import '../components/Icon/fonts/getapp-font.scss';

const AiAssistant = dynamic(() => import('components/AiAssistant'), {
  ssr: false,
});
const Error = dynamic(() => import('./_error'));
const GetAppLiveChat = dynamic(
  () => import('components/LiveChat/GetAppLiveChat'),
  {
    ssr: false,
  }
);

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const NOOP_LAYOUT = ({ children }) => children;

// Reads the Osano kill-switch flag from VercelFlagsProvider's context, so it
// must be rendered as a descendant of that provider (see below).
const OsanoConsentOverlayGate = () => {
  const osanoCmpFlagVariant = useFlag(CORE_FLAGS.OSANO_CMP_PRIMARY, -1);
  return osanoCmpFlagVariant === 1 ? <OsanoConsentOverlay /> : null;
};

let dataDogRumPromise;

const lazyDataDogRum = () => {
  if (dataDogRumPromise) {
    return dataDogRumPromise;
  }

  dataDogRumPromise = import('@datadog/browser-rum')
    .then(({ datadogRum }) => {
      datadogRum.init({
        applicationId: getConfig().datadogRum.applicationId,
        clientToken: getConfig().datadogRum.clientToken,
        site: 'datadoghq.com',
        service: 'bx-getapp',
        env: process.env.NODE_ENV,
        version: getConfig().datadogRum.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        enableExperimentalFeatures: ['feature_flags'],
      });

      window._fs_ready = () => {
        datadogRum.setUserProperty('fsUrl', window.FS.getCurrentSessionURL());
      };

      datadogRum.startSessionReplayRecording();

      return datadogRum;
    })
    .catch((error) => {
      dataDogRumPromise = undefined;
      throw error;
    });

  return dataDogRumPromise;
};

/** @type {import("./_app").default} */
const GetAppUI = ({ Component, pageProps, err }) => {
  // Workaround for https://github.com/zeit/next.js/issues/8592
  const modifiedPageProps = { ...pageProps, err };
  const Layout = Component.Layout || NOOP_LAYOUT;
  const config = getConfig();
  const router = useRouter();
  const buyerIntentSourceLocation = getBuyerIntentPageSourceLocation(
    router.pathname
  );
  const buyerIntentViewKey =
    router.asPath || router.pathname || buyerIntentSourceLocation;
  const shouldUseGenericBuyerIntentPageView = Boolean(
    pageProps.error || !Component.hasBuyerIntentPageView
  );
  // Mirrors the `/p/sem/` convention already used by ExitIntentModals and
  // Pagination for this same class of page — never show live chat there,
  // on any device, product decision not a gap.
  const isSemPage = (router.asPath || '').split('?')[0].includes('/p/sem/');
  const shouldShowLiveChat = Boolean(
    config.liveChat.enabled &&
      config.liveChat.organizationId &&
      pageProps.appData?.canShowLiveChat &&
      !isSemPage
  );
  const shouldActivateLiveChat = Boolean(
    pageProps.appData?.userAgent?.isDesktop &&
      pageProps.appData?.head?.hasLiveChatEnabled &&
      !pageProps.appData?.isDebugBear
  );
  const shouldShowAiAssistant = Boolean(
    pageProps.appData?.showAiAssistant &&
      !shouldShowLiveChat &&
      !shouldActivateLiveChat
  );

  // Auto-merge Vercel Flags GA4 experiment data with LD experiment data.
  // _vercelExperimentsGa4 is set by middleware → getAppData; experiments.ga4 by LD.
  // When no tracked flags resolve, this is a no-op (mergeExperimentsGA4 returns LD data as-is).
  const appDataWithMergedExperiments = useMemo(() => {
    const appData = pageProps.appData;
    if (!appData) return appData;

    const vercelGa4 = appData._vercelExperimentsGa4;
    if (!vercelGa4?.User_Information_GAExperimentId) return appData;

    return {
      ...appData,
      experiments: {
        ...appData.experiments,
        ga4: mergeExperimentsGA4(appData.experiments?.ga4, vercelGa4),
      },
    };
  }, [pageProps.appData]);

  const vercelFlagsForRum = useMemo(() => {
    const ga4 = pageProps.appData?._vercelExperimentsGa4;
    if (!ga4?.User_Information_GAExperimentId) return {};
    return Object.fromEntries(
      ga4.User_Information_GAExperimentId.split('|').map((entry) => {
        const lastDot = entry.lastIndexOf('.');
        return [entry.slice(0, lastDot), Number(entry.slice(lastDot + 1))];
      })
    );
  }, [pageProps.appData]);

  const [ddRumReady, setDdRumReady] = useState(false);

  useEffect(() => {
    if (!config.datadogRum.enabled) {
      return;
    }
    lazyDataDogRum()
      .then(() => setDdRumReady(true))
      .catch(() => undefined);
  }, [config.datadogRum.enabled]);

  const onChangeCompareBucket = (items) => {
    setCookie(null, 'compareBasket', JSON.stringify(items), {
      maxAge: 30 * 24 * 60 * 60,
      path: '/',
    });
  };
  const handleLiveChatEvent = (event) => {
    google.sendEvent(createLiveChatAnalyticsEvent(event));
  };

  const routedPageContent = pageProps.error ? (
    <Error statusCode={pageProps.error.statusCode} {...modifiedPageProps} />
  ) : (
    <Component {...modifiedPageProps} />
  );

  const pageContent = shouldUseGenericBuyerIntentPageView ? (
    <BuyerIntentPageView
      key={buyerIntentViewKey}
      sourceLocation={buyerIntentSourceLocation}
    >
      {routedPageContent}
    </BuyerIntentPageView>
  ) : (
    routedPageContent
  );

  return (
    <div className={getappIconFont.variable}>
      <BuyerIntentSession>
        <CompareBasketProvider
          enabled={true}
          onChange={onChangeCompareBucket}
          basket={
            (!pageProps.compareData && pageProps.appData?.compareBasketState) ||
            []
          }
        >
          <AuthSessionAppProvider value={appDataWithMergedExperiments}>
            <VercelFlagsProvider flags={pageProps.appData?._vercelFlags || {}}>
              <OsanoConsentOverlayGate />
              <Analytics
                config={config.analytics}
                appData={appDataWithMergedExperiments}
              >
                <OneTapAnalytics />
                <UnifiedLoginAnalytics />
                <ViewportProvider ssrViewport={pageProps.appData?.viewport}>
                  <ScrollMilestones />
                  {ddRumReady &&
                    !pageProps.appData?.isCypressTakingSnapshots && (
                      <DDRumIntegration featureFlags={vercelFlagsForRum} />
                    )}
                  <ExposeUserScript />
                  <ExitIntentModals />
                  {shouldShowLiveChat && (
                    <GetAppLiveChat
                      organizationId={config.liveChat.organizationId}
                      group={config.liveChat.group}
                      chatBetweenGroups={config.liveChat.chatBetweenGroups}
                      route={pageProps.appData?.route}
                      countryCode={pageProps.appData?.liveChatCountryCode}
                      isWorkingHours={pageProps.appData?.liveChatIsWorkingHours}
                      onEvent={handleLiveChatEvent}
                    />
                  )}
                  {shouldShowAiAssistant && (
                    <AiAssistant offsetForLiveChat={shouldShowLiveChat} />
                  )}
                  {pageProps.appData?.g2CompanyLookupApiKey &&
                    pageProps.appData?.g2CompanyLookupUrl && (
                      <G2CompanyLookup
                        apiKey={pageProps.appData.g2CompanyLookupApiKey}
                        url={pageProps.appData.g2CompanyLookupUrl}
                        showCached={pageProps.appData.g2EnrichHit || false}
                      />
                    )}

                  {/* CssBaseline kickstart an elegant, consistent and simple baseline to build upon */}
                  {pageProps.error ? (
                    <MainLayout>{pageContent}</MainLayout>
                  ) : (
                    <Layout>{pageContent}</Layout>
                  )}
                </ViewportProvider>
              </Analytics>
            </VercelFlagsProvider>
          </AuthSessionAppProvider>
        </CompareBasketProvider>
      </BuyerIntentSession>
      <div id="portal_root" />
    </div>
  );
};

export default GetAppUI;
